/**
 * Trial Reminder Dialog Component
 * Shows a popup reminder for free trial companies about their trial ending
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Alert,
} from '@mui/material';
import {
  Close as CloseIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { getTrialStatus } from '../../services/companySubscriptionService';

/**
 * Trial Reminder Dialog Component
 */
const TrialReminderDialog = ({ open, onClose }) => {
  const [trialData, setTrialData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (open) {
      loadTrialStatus();
    }
  }, [open]);

  const loadTrialStatus = async () => {
    try {
      setLoading(true);
      const data = await getTrialStatus();
      setTrialData(data);
    } catch (err) {
      console.error('Error loading trial status:', err);
      // If error, close dialog
      if (onClose) {
        onClose();
      }
    } finally {
      setLoading(false);
    }
  };

  if (!open || loading || !trialData) {
    return null;
  }

  const { trialActive, daysRemaining } = trialData;

  // Only show if trial is active
  if (!trialActive) {
    return null;
  }

  // Determine severity based on days remaining
  const getSeverity = () => {
    if (daysRemaining <= 3) {
      return 'warning';
    } else if (daysRemaining <= 7) {
      return 'info';
    }
    return 'success';
  };

  const getMessage = () => {
    if (daysRemaining <= 3) {
      return `Your free trial will end in ${daysRemaining} ${daysRemaining === 1 ? 'day' : 'days'}. Please renew your subscription to continue using the service.`;
    } else if (daysRemaining <= 7) {
      return `Your free trial will end in ${daysRemaining} days. Consider upgrading to continue using all features.`;
    }
    return `Your free trial will end in ${daysRemaining} days. Upgrade anytime to unlock additional features.`;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {daysRemaining <= 3 ? (
            <WarningIcon color="warning" />
          ) : (
            <ScheduleIcon color="primary" />
          )}
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Free Trial Reminder
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={onClose}
          sx={{ color: 'text.secondary' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent>
        <Alert severity={getSeverity()} sx={{ mb: 2 }}>
          <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
            {getMessage()}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Days Remaining: <strong>{daysRemaining} {daysRemaining === 1 ? 'day' : 'days'}</strong>
          </Typography>
        </Alert>
        
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            To continue using all features after your trial ends, please contact administration to activate your subscription.
          </Typography>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onClose}
          variant="contained"
          color="primary"
          fullWidth
        >
          Got it
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TrialReminderDialog;

